import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Info, KeyRound, UserCircle, ScrollText } from "lucide-react";
import LegalDocViewer from "@/components/LegalDocViewer";

const EMPTY_PW = { current: "", next: "", confirm: "" };

export default function AccountTab() {
  const { user, setUser } = useAuth();
  const [settings, setSettings] = useState(null);
  const [pwForm, setPwForm] = useState(EMPTY_PW);
  const [busy, setBusy] = useState(false);
  const [legalDocs, setLegalDocs] = useState(null);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/legal/documents").then(({ data }) => setLegalDocs(data)).catch(() => {});
  }, []);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) {
      toast.error("Le nuove password non coincidono");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: pwForm.current, new_password: pwForm.next });
      setUser(data);
      setPwForm(EMPTY_PW);
      toast.success("Password aggiornata con successo");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return <p className="text-sm text-slate-500">Caricamento...</p>;

  return (
    <div className="max-w-2xl space-y-6" data-testid="account-tab">
      <Card className="institutional-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <UserCircle className="h-5 w-5 text-[#003366]" />
            <h3 className="font-display font-semibold text-slate-900">Piano attuale</h3>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Badge data-testid="account-plan-badge" className="bg-green-100 text-green-800">Gratuito</Badge>
            <p className="text-sm text-slate-700" data-testid="account-plan-validity">
              valido fino al <strong>31 dicembre 2026</strong>
            </p>
          </div>
          {settings.plan_label && (
            <p className="text-xs text-slate-500 mt-2" data-testid="account-plan-label">{settings.plan_label}</p>
          )}
          <p data-testid="account-plan-info"
            className="mt-5 text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
            <Info className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
            <span>A gennaio 2027 saranno disponibili i piani a pagamento. Riceverai una comunicazione in anticipo.</span>
          </p>
        </CardContent>
      </Card>

      <Card className="institutional-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound className="h-5 w-5 text-[#003366]" />
            <h3 className="font-display font-semibold text-slate-900">Cambia password</h3>
          </div>
          {user?.auth_provider === "google" ? (
            <p data-testid="account-pw-google-note" className="text-sm text-slate-600">
              Questo account accede tramite Google: la password non è modificabile da qui.
            </p>
          ) : (
            <form onSubmit={changePassword} className="space-y-4" data-testid="account-change-password-form">
              <div>
                <Label htmlFor="pw-current">Password attuale</Label>
                <Input data-testid="account-pw-current" id="pw-current" type="password" required value={pwForm.current}
                  onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="pw-next">Nuova password (min. 8 caratteri)</Label>
                <Input data-testid="account-pw-new" id="pw-next" type="password" required minLength={8} value={pwForm.next}
                  onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="pw-confirm">Conferma nuova password</Label>
                <Input data-testid="account-pw-confirm" id="pw-confirm" type="password" required minLength={8} value={pwForm.confirm}
                  onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} />
              </div>
              <Button data-testid="account-pw-submit" type="submit" disabled={busy} className="bg-[#003366] hover:bg-[#002244]">
                {busy ? "Aggiornamento..." : "Aggiorna password"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>

      <Card className="institutional-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <ScrollText className="h-5 w-5 text-[#003366]" />
            <h3 className="font-display font-semibold text-slate-900">Documenti legali</h3>
          </div>
          <ul className="space-y-2 text-sm" data-testid="account-legal-docs">
            {(legalDocs || []).map((d) => (
              <li key={d.doc_type} className="flex flex-wrap items-center gap-2">
                <LegalDocViewer doc={d} label={d.title} testid={`legal-view-${d.doc_type}`} />
                <Badge className="bg-slate-100 text-slate-600">v{d.version}</Badge>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
